import { useState } from "react";
import type { Participant } from "@/api/types";
import { Avatar } from "./Avatar";
import { ParticipantPicker } from "./ParticipantPicker";
import "./paid-by-slot.css";

export interface PaidBySlotProps {
  /** The chosen payer, or null if not set yet. */
  participant: Participant | null;
  /** Full participant roster for the picker. */
  participants: ReadonlyArray<Participant>;
  /** Read-only mode disables the tap → picker affordance. */
  disabled?: boolean;
  /** Called with the chosen participant_id when the user picks one. */
  onChange: (participantId: string) => void;
}

/**
 * Compact row in the receipt header showing who paid. Per components.md
 * §7.6: a chip when set, a red "Не вказано хто платив" badge when null.
 * Tap opens the ParticipantPicker.
 */
export function PaidBySlot({
  participant,
  participants,
  disabled,
  onChange,
}: PaidBySlotProps) {
  const [pickerOpen, setPickerOpen] = useState(false);

  function handleOpen() {
    if (disabled) return;
    setPickerOpen(true);
  }

  function handlePick(pid: string) {
    setPickerOpen(false);
    onChange(pid);
  }

  return (
    <div className="wp-paidby">
      <span className="wp-paidby__label">Хто платив</span>
      <button
        type="button"
        className="wp-paidby__trigger"
        onClick={handleOpen}
        disabled={disabled}
        aria-label={
          participant
            ? `Хто платив: ${participant.name}. Натисни щоб змінити.`
            : "Не вказано хто платив. Натисни щоб обрати."
        }
        data-testid="paid-by-slot"
      >
        {participant ? (
          <span className="wp-paidby__chip">
            <Avatar participant={participant} size="sm" />
            <span className="wp-paidby__name">{participant.name}</span>
          </span>
        ) : (
          <span className="wp-paidby__missing">Не вказано хто платив</span>
        )}
      </button>
      <ParticipantPicker
        open={pickerOpen}
        participants={participants}
        selectedId={participant?.participant_id ?? null}
        onPick={handlePick}
        onCancel={() => setPickerOpen(false)}
      />
    </div>
  );
}
